import {
  AffiliateProduct,
  getActiveProducts,
  getProductById,
} from "./affiliateProducts";

export type AffiliatePlacement =
  | "home"
  | "calc-point"
  | "convert-10-to-4"
  | "gpa-cpa"
  | "practice-home"
  | "practice-summary"
  | "notes"
  | "blog"
  | "shop-page"
  | "interstitial";

/**
 * Sản phẩm nào hợp ngữ cảnh nào, xếp theo thứ tự ưu tiên.
 *
 * Nguyên tắc: chỉ ghép sản phẩm khi nhận xét của admin thật sự liên quan tới
 * việc người dùng đang làm trên trang đó. "bong-tay-trang-cotton" cố ý không
 * xuất hiện ở các trang công cụ học tập vì không có ngữ cảnh nào biện minh được.
 */
export const PLACEMENT_PRODUCTS: Record<AffiliatePlacement, string[]> = {
  // Đang ôn thi — nhận xét "mình dùng chính con này để ôn thi TOEIC" khớp trực tiếp
  "practice-home": ["tai-nghe-lenovo-th10"],
  "practice-summary": ["tai-nghe-lenovo-th10", "bong-tay-trang-cotton"],
  // Đang ngồi máy nhập điểm / tính toán
  "calc-point": ["chuot-logitech-b100"],
  "convert-10-to-4": ["chuot-logitech-b100"],
  "gpa-cpa": ["chuot-logitech-b100"],
  // Đang ghi chép, học
  notes: ["sach-sapiens"],
  blog: ["sach-sapiens", "tai-nghe-lenovo-th10"],
  // Vị trí trung tính
  home: ["bong-tay-trang-cotton", "sach-sapiens"],
  interstitial: [
    "tai-nghe-lenovo-th10",
    "bong-tay-trang-cotton",
    "sach-sapiens",
    "chuot-logitech-b100",
  ],
  "shop-page": [],
};

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const getDayIndex = (): number => Math.floor(Date.now() / 86_400_000);

const resolvePool = (placement: AffiliatePlacement): AffiliateProduct[] => {
  const ids = PLACEMENT_PRODUCTS[placement];
  if (!ids || ids.length === 0) return getActiveProducts();
  return ids
    .map(getProductById)
    .filter(
      (product): product is AffiliateProduct =>
        !!product && product.active !== false
    );
};

/**
 * Xoay vòng deterministic theo ngày: ổn định trong cùng một ngày (không nhấp
 * nháy giữa các lần render), đổi sang sản phẩm khác vào ngày hôm sau.
 */
export const getProductsForPlacement = (
  placement: AffiliatePlacement,
  count: number = 1,
  excludeIds: string[] = []
): AffiliateProduct[] => {
  const pool = resolvePool(placement);
  if (pool.length === 0) return [];

  // Ưu tiên sản phẩm chưa click gần đây; nếu loại hết thì quay lại dùng cả pool.
  const filtered = pool.filter((product) => !excludeIds.includes(product.id));
  const candidates = filtered.length > 0 ? filtered : pool;

  const offset = getDayIndex() + hashString(placement);
  const picked: AffiliateProduct[] = [];
  const take = Math.min(count, candidates.length);
  for (let i = 0; i < take; i += 1) {
    picked.push(candidates[(offset + i) % candidates.length]);
  }
  return picked;
};

export const getShopProducts = (): AffiliateProduct[] =>
  getActiveProducts()
    .slice()
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

export { getProductById };
